import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import '../caria.css';
import { BottomNav, ColorRail, SELLER_NAV, TopBar } from '../../../commodity/components/caria';
import { VehicleDraftForm } from '../types';
import VehicleDraftCreate from './VehicleDraftCreate';
import {
  createDraft,
  fetchListing,
  activateListing,
  submitDisclosure,
  updateListing,
  uploadPhoto,
  verifyVin,
  ApiFuelType,
  ApiTransmission,
  CreateDraftInput,
  PhotoCategory,
  REQUIRED_PHOTO_CATEGORIES,
  SellerListing,
} from '../api/seller.api';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

/**
 * VehiclePublish (BC-01-E01/E02/E04/E05) — the live seller publishing flow.
 *
 * Closes the Week-2 loop: PI-1 built the whole `/vehicles/*` backend
 * (draft → verify-vin → 6 photos → disclosure → price → activate) but the
 * only way to drive it was the seed script. This screen wires that surface
 * to the UI so a seller can create and publish a listing end to end.
 *
 * Two modes off one component:
 *   /publish                → create the DRAFT (reuses VehicleDraftCreate)
 *   /vehicles/:id/publish   → complete the 5 activation pre-conditions
 *
 * Photo upload is stub-aware (see uploadPhoto): against dev stub storage the
 * PUT is skipped, so the flow is demoable before real S3 lands (Week-1 B2).
 */

// The create form carries Spanish labels; the API wants kernel enums.
const TRANSMISSION_MAP: Record<string, ApiTransmission> = {
  Automática: 'AUTOMATIC',
  Manual: 'MANUAL',
  CVT: 'CVT',
};
const FUEL_MAP: Record<string, ApiFuelType> = {
  Gasolina: 'GASOLINE',
  Diésel: 'DIESEL',
  Híbrido: 'HYBRID',
  Eléctrico: 'ELECTRIC',
};

const PHOTO_LABEL: Record<PhotoCategory, string> = {
  EXTERIOR_FRONT: 'Exterior frontal',
  EXTERIOR_REAR: 'Exterior trasero',
  INTERIOR_DASHBOARD: 'Tablero',
  INTERIOR_SEATS: 'Asientos',
  ENGINE: 'Motor',
  TIRES: 'Llantas',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  ARCHIVED: 'Archivado',
  SUSPENDED: 'Suspendido',
};

const toCreateInput = (form: VehicleDraftForm): CreateDraftInput => {
  const vin = form.vin.trim().toUpperCase();
  if (vin.length < 11 || vin.length > 17) {
    throw new Error('El VIN es obligatorio para crear el borrador (11–17 caracteres).');
  }
  const transmission = TRANSMISSION_MAP[form.transmission];
  const fuel = FUEL_MAP[form.fuel];
  if (!transmission) throw new Error('Selecciona una transmisión válida.');
  if (!fuel) throw new Error('Selecciona un combustible válido.');
  return {
    make: form.make.trim(),
    model: form.model.trim(),
    year: Number(form.year),
    mileage: Number(form.mileageKm),
    transmission,
    fuel,
    color: form.color.trim(),
    vin,
  };
};

const StepBadge: React.FC<{ done: boolean }> = ({ done }) => (
  <span className={`caria-tab__badge${done ? ' caria-tab__badge--done' : ''}`} aria-hidden>
    {done ? '✓' : '•'}
  </span>
);

// ── Create mode ──────────────────────────────────────────────────────────────

const CreateMode: React.FC = () => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleCreate = async (form: VehicleDraftForm) => {
    setError(undefined);
    let input: CreateDraftInput;
    try {
      input = toCreateInput(form);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Datos inválidos.');
      return;
    }
    setSubmitting(true);
    try {
      const id = await createDraft(input);
      navigate(`/vehicles/${id}/publish`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el borrador.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cia-sell cia-sell--form">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />
      {error && (
        <p className="caria-publish-step__hint" role="alert" data-testid="create-error">
          {error}
        </p>
      )}
      <VehicleDraftCreate
        submitting={submitting}
        onCreate={handleCreate}
        onCancel={() => navigate('/my-listings')}
      />
      <BottomNav />
    </div>
  );
};

// ── Edit / publish mode ──────────────────────────────────────────────────────

const EditMode: React.FC<{ id: string }> = ({ id }) => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [listing, setListing] = useState<SellerListing | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [msg, setMsg] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  // Photos + disclosure have no read endpoint yet, so completion for those is
  // tracked locally within the session (create → publish happens in one go).
  const [photos, setPhotos] = useState<Set<PhotoCategory>>(new Set());
  const [disclosureDone, setDisclosureDone] = useState(false);
  const [price, setPrice] = useState('');
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    setLoadError(undefined);
    try {
      const l = await fetchListing(id);
      setListing(l);
      if (l.listingPrice != null) setPrice(String(l.listingPrice));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'No se pudo cargar el listing.');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    setMsg(undefined);
    try {
      await fn();
      if (ok) setMsg(ok);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'La acción no se pudo completar.');
      throw e;
    } finally {
      setBusy(undefined);
    }
  };

  if (loadError) {
    return (
      <div className="cia-sell cia-sell--form">
        <TopBar {...authChrome} nav={SELLER_NAV} />
        <ColorRail />
        <p className="caria-publish-step__hint" role="alert">
          {loadError}
        </p>
        <BottomNav />
      </div>
    );
  }
  if (!listing) {
    return (
      <div className="cia-sell cia-sell--form">
        <TopBar {...authChrome} nav={SELLER_NAV} />
        <ColorRail />
        <p className="caria-publish__lead">Cargando…</p>
        <BottomNav />
      </div>
    );
  }

  const vinDone = listing.vinVerified;
  const photosDone = REQUIRED_PHOTO_CATEGORIES.every((c) => photos.has(c));
  const priceDone = listing.listingPrice != null;
  const canActivate = vinDone && photosDone && disclosureDone && priceDone;

  const onVerifyVin = () =>
    run(
      'vin',
      async () => {
        const verified = await verifyVin(id);
        await load();
        if (!verified) throw new Error('El VIN no pudo verificarse. Revisa el número.');
      },
      'VIN verificado.',
    ).catch(() => undefined);

  const onPickPhoto = (cat: PhotoCategory) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    run(
      `photo:${cat}`,
      async () => {
        await uploadPhoto(id, cat, file);
        setPhotos((prev) => new Set(prev).add(cat));
      },
      `Foto "${PHOTO_LABEL[cat]}" cargada.`,
    ).catch(() => undefined);
  };

  const onSaveDisclosure = () =>
    run(
      'disclosure',
      async () => {
        await submitDisclosure(id, {
          accidentHistory: false,
          previousOwners: 1,
          serviceRecordsAvailable: true,
          outstandingLiens: false,
          knownDefects: 'Sin defectos conocidos declarados por el vendedor.',
        });
        setDisclosureDone(true);
      },
      'Declaración guardada.',
    ).catch(() => undefined);

  const onSavePrice = () =>
    run(
      'price',
      async () => {
        const value = Number(price);
        if (!Number.isFinite(value) || value <= 0) throw new Error('Indica un precio válido.');
        await updateListing(id, { listingPrice: value });
        await load();
      },
      'Precio guardado.',
    ).catch(() => undefined);

  const onActivate = () =>
    run('activate', async () => {
      await activateListing(id);
      navigate('/my-listings');
    }).catch(() => undefined);

  const StepHead: React.FC<{ n: number; title: string; done: boolean }> = ({ n, title, done }) => (
    <div className="caria-publish-step__head">
      <h2>
        <StepBadge done={done} /> {n}. {title}
      </h2>
    </div>
  );

  return (
    <div className="cia-sell cia-sell--form">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />

      <header className="caria-publish__header">
        <div>
          <div className="cia-sell-eye">Publicar vehículo</div>
          <h1>
            {listing.make} {listing.model} {listing.year}
          </h1>
        </div>
        <span className={`cia-sell-status cia-sell-st-${listing.status.toLowerCase()}`}>
          {STATUS_LABEL[listing.status] ?? listing.status}
        </span>
      </header>

      {msg && (
        <p className="caria-publish-step__hint" role="status" data-testid="publish-msg">
          {msg}
        </p>
      )}

      {/* 1 · VIN */}
      <section className="caria-publish-step" data-testid="step-vin">
        <StepHead n={1} title="Verificación de VIN" done={vinDone} />
        <p className="caria-publish__lead">VIN: {listing.make ? '••••' : ''}</p>
        {vinDone ? (
          <p className="caria-publish-step__hint">VIN verificado ✓</p>
        ) : (
          <button
            type="button"
            className="caria-cta caria-cta--inline"
            disabled={busy === 'vin'}
            data-testid="verify-vin"
            onClick={onVerifyVin}
          >
            {busy === 'vin' ? 'Verificando…' : 'Verificar VIN'}
          </button>
        )}
      </section>

      {/* 2 · Photos */}
      <section className="caria-publish-step" data-testid="step-photos">
        <StepHead n={2} title={`Fotos requeridas (${photos.size}/6)`} done={photosDone} />
        <ul className="caria-photo-grid">
          {REQUIRED_PHOTO_CATEGORIES.map((cat) => {
            const present = photos.has(cat);
            const uploading = busy === `photo:${cat}`;
            return (
              <li
                key={cat}
                className={`caria-photo-slot${present ? ' caria-photo-slot--present' : ''}`}
                data-testid={`photo-${cat}${present ? '-present' : '-missing'}`}
              >
                <span className="caria-photo-slot__icon" aria-hidden>
                  {present ? '✓' : uploading ? '…' : '+'}
                </span>
                <span>{PHOTO_LABEL[cat]}</span>
                {!present && (
                  <>
                    <button
                      type="button"
                      className="caria-btn-secondary caria-cta--inline"
                      disabled={uploading}
                      onClick={() => fileInputs.current[cat]?.click()}
                    >
                      {uploading ? 'Subiendo…' : 'Subir'}
                    </button>
                    <input
                      ref={(el) => {
                        fileInputs.current[cat] = el;
                      }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      data-testid={`photo-input-${cat}`}
                      onChange={onPickPhoto(cat)}
                    />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* 3 · Disclosure */}
      <section className="caria-publish-step" data-testid="step-disclosure">
        <StepHead n={3} title="Declaración del vendedor" done={disclosureDone} />
        <p className="caria-publish__lead">
          Declara accidentes, dueños previos, mantenimiento y gravámenes. (Demo: valores por defecto
          “sin incidencias”.)
        </p>
        {disclosureDone ? (
          <p className="caria-publish-step__hint">Declaración guardada ✓</p>
        ) : (
          <button
            type="button"
            className="caria-cta caria-cta--inline"
            disabled={busy === 'disclosure'}
            data-testid="save-disclosure"
            onClick={onSaveDisclosure}
          >
            {busy === 'disclosure' ? 'Guardando…' : 'Guardar declaración'}
          </button>
        )}
      </section>

      {/* 4 · Price */}
      <section className="caria-publish-step" data-testid="step-price">
        <StepHead n={4} title="Precio de venta" done={priceDone} />
        {listing.suggestedPrice != null && (
          <p className="caria-publish-step__hint">
            Precio sugerido (Tasador): ${Math.round(listing.suggestedPrice).toLocaleString('en-US')}
          </p>
        )}
        <label className="caria-publish-field">
          <span>Precio (USD)</span>
          <input
            type="number"
            inputMode="numeric"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="18500"
            data-testid="price-input"
          />
        </label>
        <button
          type="button"
          className="caria-cta caria-cta--inline"
          disabled={busy === 'price'}
          data-testid="save-price"
          onClick={onSavePrice}
        >
          {busy === 'price' ? 'Guardando…' : 'Guardar precio'}
        </button>
      </section>

      {/* 5 · Activate */}
      <section className="caria-publish-step" data-testid="step-activate">
        <StepHead n={5} title="Activar anuncio" done={listing.status === 'ACTIVE'} />
        {!canActivate && (
          <ul className="caria-precondition-list" data-testid="activate-missing">
            {!vinDone && <li className="caria-precondition-list__item">Verifica el VIN.</li>}
            {!photosDone && (
              <li className="caria-precondition-list__item">Sube las 6 fotos requeridas.</li>
            )}
            {!disclosureDone && (
              <li className="caria-precondition-list__item">Completa la declaración.</li>
            )}
            {!priceDone && (
              <li className="caria-precondition-list__item">Define el precio de venta.</li>
            )}
          </ul>
        )}
        <button
          type="button"
          className="caria-cta caria-cta--success"
          disabled={!canActivate || busy === 'activate'}
          data-testid="activate-cta"
          onClick={onActivate}
        >
          {busy === 'activate' ? 'Activando…' : 'Activar anuncio'}
        </button>
      </section>

      <BottomNav />
    </div>
  );
};

export const VehiclePublish: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <EditMode id={id} /> : <CreateMode />;
};

export default VehiclePublish;
